import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditIcon from '@mui/icons-material/Edit';
import VisibilityOutlined from '@mui/icons-material/VisibilityOutlined';
import {
  GridActionsCellItem,
  GridColDef,
  GridRenderCellParams,
  GridRowParams,
} from '@mui/x-data-grid-pro';
import { useMemo } from 'react';
import MetaInfoDisplay from '../utils/meta-info-display';
import { ProcedureCodeRow } from './use-procedure-code-queries';

interface UseProcedureCodeColumnsProps {
  onEdit?: (row: ProcedureCodeRow) => void;
  onDelete?: (row: ProcedureCodeRow) => void;
  onViewApplicability?: (row: ProcedureCodeRow) => void;
  canWrite: boolean;
}

export const useProcedureCodeColumns = ({
  onEdit,
  onDelete,
  onViewApplicability,
  canWrite,
}: UseProcedureCodeColumnsProps): GridColDef[] =>
  useMemo(
    () => [
      {
        field: 'partnershipName',
        headerName: 'Partnership',
        flex: 1.2,
        minWidth: 150,
      },
      {
        field: 'insurerName',
        headerName: 'Insurer',
        flex: 1.2,
        minWidth: 150,
      },
      {
        field: 'clientName',
        headerName: 'Client',
        flex: 1.2,
        minWidth: 150,
      },
      {
        field: 'cptCode',
        headerName: 'CPT Code',
        flex: 0.8,
        minWidth: 110,
      },
      {
        field: 'paymentTypeName',
        headerName: 'Payment Type',
        flex: 1,
        minWidth: 130,
      },
      {
        field: 'modifier1',
        headerName: 'Modifier 1',
        flex: 0.7,
        minWidth: 100,
        renderCell: (params): string => params.value ?? '--',
      },
      {
        field: 'modifier2',
        headerName: 'Modifier 2',
        flex: 0.7,
        minWidth: 100,
        renderCell: (params): string => params.value ?? '--',
      },
      {
        field: 'info',
        headerName: '',
        width: 50,
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        renderCell: (
          params: GridRenderCellParams<ProcedureCodeRow>,
        ): JSX.Element => (
          <MetaInfoDisplay
            lastModifiedBy={params.row.lastModifiedBy ?? undefined}
            updatedAt={params.row.updatedAt}
            createdAt={params.row.createdAt}
          />
        ),
      },
      {
        field: 'actions',
        headerName: 'Actions',
        type: 'actions',
        width: 130,
        getActions: (
          params: GridRowParams<ProcedureCodeRow>,
        ): JSX.Element[] => [
          <GridActionsCellItem
            key={`view-applicability-${params.id}`}
            icon={<VisibilityOutlined />}
            label="View Applicability"
            onClick={(): void => onViewApplicability?.(params.row)}
          />,
          ...(canWrite
            ? [
                <GridActionsCellItem
                  key={`edit-${params.id}`}
                  icon={<EditIcon />}
                  label="Edit"
                  onClick={(): void => onEdit?.(params.row)}
                />,
                <GridActionsCellItem
                  key={`delete-${params.id}`}
                  icon={<DeleteOutlineIcon />}
                  label="Delete"
                  onClick={(): void => onDelete?.(params.row)}
                />,
              ]
            : []),
        ],
      },
    ],
    [onEdit, onDelete, onViewApplicability, canWrite],
  );
